// Session location store.
//
// The legacy backend scopes most transactional endpoints (sales, purchases,
// payments, expenses, item search, trails…) to `session('location_id')`, set by
// `POST app/location`. The Vue admin forced the user to pick a location; this
// store does the same for React: it loads the selectable locations, restores
// the last choice (or the user's default), and exposes `ensureLocation()` for
// any call that sits behind the `location` middleware.

import { useSyncExternalStore } from 'react';

import { adminApi } from '../api/client';

export type SessionLocation = { value: string; label: string; [key: string]: unknown };

type LocationState = {
  locations: SessionLocation[];
  currentId: string | null;
  loaded: boolean;
};

const STORAGE_KEY = 'vendia-location-id';

let state: LocationState = { locations: [], currentId: null, loaded: false };
const listeners = new Set<() => void>();
let initPromise: Promise<void> | null = null;

function emit(next: Partial<LocationState>) {
  state = { ...state, ...next };
  listeners.forEach((fn) => fn());
}

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

async function loadLocations(): Promise<SessionLocation[]> {
  const response = await adminApi.get('locations/search');
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows
    .filter((row: Record<string, unknown>) => row && (row.value ?? row.id))
    .map((row: Record<string, unknown>) => ({ ...row, value: String(row.value ?? row.id), label: String(row.label ?? row.name ?? row.value) }));
}

/** Select a location for the current backend session. */
export async function selectLocation(id: string) {
  await adminApi.post('location', { location_id: id });
  writeStored(id);
  emit({ currentId: id });
  window.dispatchEvent(new CustomEvent('vendia:location-changed', { detail: { id } }));
}

/**
 * Make sure the backend session has a location selected. Resolves once
 * `session('location_id')` is set. Safe to call before every location-gated request.
 */
export function ensureLocation(preferredId?: string | null): Promise<void> {
  if (state.loaded && state.currentId) return Promise.resolve();
  if (!initPromise) {
    initPromise = (async () => {
      const [locations, current] = await Promise.all([
        loadLocations(),
        adminApi.get('current_location').then((r) => (r.data?.location_id as string | null) ?? null).catch(() => null)
      ]);
      emit({ locations });
      if (current && locations.some((l) => l.value === current)) {
        emit({ currentId: current, loaded: true });
        return;
      }
      const stored = readStored();
      const pick =
        [stored, preferredId].find((id) => id && locations.some((l) => l.value === id)) ?? locations[0]?.value ?? null;
      if (!pick) {
        emit({ loaded: true });
        throw new Error('No location is available for this account.');
      }
      await selectLocation(pick);
      emit({ loaded: true });
    })().catch((error) => {
      initPromise = null;
      throw error;
    });
  }
  return initPromise;
}

/** Forget cached state (e.g. after logout). */
export function resetLocationState() {
  initPromise = null;
  emit({ locations: [], currentId: null, loaded: false });
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function snapshot() {
  return state;
}

/** React hook exposing the session location state. */
export function useSessionLocation() {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Re-run an effect when the session location changes. */
export function onLocationChanged(fn: () => void) {
  const handler = () => fn();
  window.addEventListener('vendia:location-changed', handler);
  return () => window.removeEventListener('vendia:location-changed', handler);
}
